import { InformationCircleIcon } from "@heroicons/react/24/outline";
import SelectComponent from "@via/components/select/select";
import { useAppsList } from "@via/pages/apps/apps.api";
import { requiredValidation } from "@via/utils/form-validators";
import { OutputParameterPayload } from "@via/utils/interfaces";
import { AppListResponseBodyItem, AppViewType } from "common";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { useDebounceCallback } from "usehooks-ts";

export const AppConfigurationForm = ({
  isAppSelected,
  pubWebApp,
  register,
  control,
  errors,
  isDisabled,
}: {
  isAppSelected: boolean;
  pubWebApp: OutputParameterPayload["pubWebApp"];
  register: UseFormRegister<OutputParameterPayload>;
  control: Control<OutputParameterPayload>;
  errors: FieldErrors<OutputParameterPayload>;
  isDisabled: boolean;
}) => {
  const [appsPage, setAppsPage] = useState(0);
  const appOptionSize = 20;
  const [searchedInput, setSearchedInput] = useState("");
  const [cumulativeApps, setCumulativeApps] = useState<
    AppListResponseBodyItem[]
  >([]);
  const [missingApps, setMissingApps] = useState<AppListResponseBodyItem[]>([]);
  const { data: appsListData, isLoading: isAppsListLoading } = useAppsList(
    appsPage,
    appOptionSize,
    [],
    [
      { id: "name", value: searchedInput },
      { id: "type", value: AppViewType.NonAdminDefault },
    ]
  );
  // Fetch missing selected apps separately
  const { data: missingAppsListData, isLoading: isMissingAppsLoading } =
    useAppsList(
      0,
      100, // large number to fetch all missing selected apps at once
      [], // no sort
      [
        { id: "type", value: AppViewType.NonAdminDefault },
        { id: "ids", value: pubWebApp?.app ?? [] },
      ],
      pubWebApp?.app?.length ? true : false // only fetch if there are missing apps
    );

  useEffect(() => {
    if (missingAppsListData?.data) {
      setMissingApps(missingAppsListData.data);
    }
  }, [missingAppsListData]);

  const fetchMoreApps = useCallback(() => {
    setAppsPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((appsListData?.total ?? 0) / appOptionSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [appsListData?.total]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchedInput) return;
    setAppsPage(0);
    setSearchedInput(value);
  }, 300);

  useEffect(() => {
    if (appsListData?.data) {
      setCumulativeApps(prevApps => {
        // Append new results while preventing duplicates
        const existingIds = new Set(prevApps.map(p => p.id));
        const newApps = appsListData?.data?.filter(p => !existingIds.has(p.id));

        return [...prevApps, ...newApps];
      });
    }
  }, [appsListData?.data, searchedInput, appsPage]);

  const appListOptions = useMemo(() => {
    const map = new Map<number, AppListResponseBodyItem>();
    cumulativeApps.forEach(app => map.set(app.id, app));
    missingApps.forEach(app => map.set(app.id, app));
    const mergedApps = Array.from(map.values());

    return (
      mergedApps
        .map(app => {
          if (app.revisions) {
            return app.revisions.map(revision => ({
              label: revision.name,
              value: revision.id,
              imageContainer: revision.image || "",
              version: revision.version || "",
            }));
          } else {
            return {
              label: app.name,
              value: app.id,
              imageContainer: app.image || "",
              version: app.version || "",
            };
          }
        })
        ?.flat() ?? []
    );
  }, [cumulativeApps, missingApps]);

  if (!isAppSelected) return null;

  return (
    <>
      <div className="my-6 flex flex-row items-center">
        <label className="flex w-64 flex-row items-center text-sm font-medium leading-6 text-gray-900">
          App Name
        </label>
        <div className="mt-2 w-full">
          <SelectComponent
            isMulti
            isLoading={isAppsListLoading || isMissingAppsLoading}
            control={control}
            name="pubWebApp.app"
            placeholder="Select App"
            options={appListOptions}
            enableClear={false}
            rules={requiredValidation("App")}
            readOnly={isDisabled}
            formatOptionLabel={(option: any) => (
              <>
                <div>
                  {option?.label}({option?.version})
                </div>
                <div className="text-xs text-gray-500">
                  {option?.imageContainer}
                </div>
              </>
            )}
            onFetchMoreOptions={fetchMoreApps}
            onInputChange={inputValue => debouncedHandleInputChange(inputValue)}
          />
          {errors.pubWebApp?.app && (
            <span className="text-xs text-rose-500">
              {errors.pubWebApp?.app.message}
            </span>
          )}
        </div>
      </div>
      <div className="my-6 flex flex-row items-center">
        <label className="flex w-64 flex-row items-center text-sm font-medium leading-6 text-gray-900">
          App File Extension
        </label>
        <div className="mt-2 w-full">
          <input
            {...register("pubWebApp.extension")}
            type="text"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            defaultValue={pubWebApp?.extension}
            disabled={isDisabled}
          />
        </div>
      </div>
      <div className="my-6 flex flex-row items-center">
        <label className="flex w-64 flex-row items-center text-sm font-medium leading-6 text-gray-900">
          <Controller
            control={control}
            name="pubWebApp.mountAppFile"
            defaultValue={pubWebApp?.mountAppFile}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <input
                type="checkbox"
                className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                checked={value === "true"}
                onChange={e => onChange(e.target.checked.toString())}
                onBlur={onBlur}
                ref={ref}
                disabled={isDisabled}
              />
            )}
          />
          Add Shortcut
          <InformationCircleIcon
            id="tooltip-mount-app-file"
            className="h-5 w-5 ml-1"
          />
          <Tooltip
            anchorSelect="#tooltip-mount-app-file"
            content="This will link the selected file's Folder to the the container's Mount Point"
          />
        </label>
      </div>
    </>
  );
};
